// Banks.jsx
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaBuilding } from "react-icons/fa";
import Swal from 'sweetalert2';

function KhatabookBank() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Modal states
    const [bankModalOpen, setBankModalOpen] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchValue: '',
        type: '',
        status: ''
    });

    // Type options
    const typeOptions = [
        { id: '', name: 'All Types' },
        { id: 'Current', name: 'Current' },
        { id: 'Saving', name: 'Saving' },
        { id: 'Others', name: 'Others' }
    ];

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Form state for add/edit
    const [formData, setFormData] = useState({
        name: '',
        account: '',
        ifsc: '',
        type: 'Saving',
        bank: '',
        status: '1'
    });

    // Fetch Banks with pagination
    const fetchBanks = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                type: filters.type,
                status: filters.status,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "banks",
                params,
                true
            );

            if (result.status === true) {
                setBanks(result.data || []);
                setFilteredBanks(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch banks");
                setBanks([]);
                setFilteredBanks([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setBanks([]);
            setFilteredBanks([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Add Bank
    const handleAddBank = () => {
        setIsEditMode(false);
        setFormData({
            name: '',
            account: '',
            ifsc: '',
            type: 'Saving',
            bank: '',
            status: '1'
        });
        setSelectedBank(null);
        setBankModalOpen(true);
    };

    // Handle Edit Bank
    const handleEditBank = (bank) => {
        setIsEditMode(true);
        setSelectedBank(bank);
        setFormData({
            name: bank.name || '',
            account: bank.account || '',
            ifsc: bank.ifsc || '',
            type: bank.type || 'Saving',
            bank: bank.bank || '',
            status: bank.status_id || '1'
        });
        setBankModalOpen(true);
    };

    // Handle Delete Bank
    const handleDeleteBank = (bank) => {
        Swal.fire({
            title: 'Delete Bank Account?',
            text: `Are you sure you want to delete "${bank.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-bank",
                        {
                            bank_id: bank.bank_id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Bank account deleted successfully!");
                        fetchBanks(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete bank account");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Status Toggle
    const handleStatusToggle = (bank) => {
        const newStatus = bank.status === 'Active' ? '0' : '1';
        const statusName = bank.status === 'Active' ? 'Inactive' : 'Active';

        Swal.fire({
            title: `Toggle Status?`,
            text: `Are you sure you want to change "${bank.name}" to ${statusName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Toggle!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "toggle-bank-status",
                        {
                            bank_id: bank.bank_id,
                            status: newStatus
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Bank account ${statusName} successfully!`);
                        fetchBanks(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to toggle status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Save Bank
    const handleSaveBank = async () => {
        if (!formData.name.trim()) {
            toast.error("Please enter account holder name");
            return;
        }

        if (!formData.account.trim()) {
            toast.error("Please enter account number");
            return;
        }

        if (formData.account.length < 9 || formData.account.length > 18) {
            toast.error("Account number should be between 9 to 18 digits");
            return;
        }

        if (!formData.ifsc.trim()) {
            toast.error("Please enter IFSC code");
            return;
        }

        if (formData.ifsc.length !== 11) {
            toast.error("IFSC code must be 11 characters");
            return;
        }

        if (!formData.bank.trim()) {
            toast.error("Please enter bank name");
            return;
        }

        if (!formData.type) {
            toast.error("Please select account type");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-bank" : "add-bank";
            const params = isEditMode
                ? {
                    bank_id: selectedBank.bank_id,
                    name: formData.name,
                    account: formData.account,
                    ifsc: formData.ifsc,
                    type: formData.type,
                    bank: formData.bank,
                    status: formData.status
                }
                : {
                    name: formData.name,
                    account: formData.account,
                    ifsc: formData.ifsc,
                    type: formData.type,
                    bank: formData.bank,
                    status: formData.status
                };

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Bank account updated successfully!" : "Bank account added successfully!");
                setBankModalOpen(false);
                fetchBanks(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save bank account");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchBanks(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchBanks(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            type: '',
            status: ''
        });
        fetchBanks(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchBanks(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get type badge color
    const getTypeColor = (type) => {
        const colors = {
            'Current': 'bg-blue-100 text-blue-800',
            'Saving': 'bg-green-100 text-green-800',
            'Others': 'bg-purple-100 text-purple-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bank Accounts</h1>
                            <p className="text-gray-600 text-sm">View and manage all bank accounts</p>
                        </div>
                        <button
                            onClick={handleAddBank}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Bank Account
                        </button>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by holder, bank, IFSC..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                                    <select
                                        value={filters.type}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {typeOptions.map(option => (
                                            <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 items-end">
                                    <button onClick={applyFilters} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">Apply</button>
                                    <button onClick={resetFilters} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Reset</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredBanks.length}</span> bank accounts
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Banks Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Holder</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Number</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IFSC</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredBanks.length > 0 ? (
                                        filteredBanks.map((bank) => (
                                            <tr key={bank.bank_id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">#{bank.bank_id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-900">{bank.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        {bank.account}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1 text-sm font-mono text-gray-600">
                                                        {bank.ifsc}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        {bank.bank}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(bank.type)}`}>
                                                        {bank.type}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(bank.status)}`}>
                                                            {bank.status}
                                                        </span>
                                                        <button onClick={() => handleStatusToggle(bank)} className="text-gray-500 hover:text-blue-600 transition-colors">
                                                            {bank.status === 'Active' ? <FaToggleOn className="text-2xl text-green-600" /> : <FaToggleOff className="text-2xl text-gray-400" />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => handleEditBank(bank)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                            <FaEdit className="text-sm" />
                                                        </button>
                                                        <button onClick={() => handleDeleteBank(bank)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                            <FaTrash className="text-sm" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                    </svg>
                                                    <p className="text-sm">No bank accounts found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                    Previous
                                </button>
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) pageNum = i + 1;
                                        else if (pagination.currentPage <= 3) pageNum = i + 1;
                                        else if (pagination.currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                                        else pageNum = pagination.currentPage - 2 + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Bank Modal */}
            {bankModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setBankModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Bank Account' : 'Add New Bank Account'}</h3>
                                    <p className="text-sm text-gray-500">{isEditMode ? `Editing ${selectedBank?.name}` : 'Create a new bank account'}</p>
                                </div>
                                <button onClick={() => setBankModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    {/* Account Holder Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter account holder name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Account Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Number <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={formData.account}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                if (value.length <= 18) {
                                                    setFormData({ ...formData, account: value });
                                                }
                                            }}
                                            placeholder="Enter account number (9-18 digits)..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            maxLength="18"
                                        />
                                        <p className="text-xs text-gray-400 mt-1">Must be between 9 to 18 digits</p>
                                    </div>

                                    {/* IFSC Code */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={formData.ifsc}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                if (value.length <= 11) {
                                                    setFormData({ ...formData, ifsc: value });
                                                }
                                            }}
                                            placeholder="Enter 11-character IFSC code..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm uppercase"
                                            maxLength="11"
                                        />
                                        <p className="text-xs text-gray-400 mt-1">Must be exactly 11 characters (e.g., SBIN0001234)</p>
                                    </div>

                                    {/* Bank Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={formData.bank}
                                            onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                                            placeholder="Enter bank name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Account Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Type <span className="text-red-500">*</span></label>
                                        <select
                                            value={formData.type}
                                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none"
                                        >
                                            <option value="Current">Current</option>
                                            <option value="Saving">Saving</option>
                                            <option value="Others">Others</option>
                                        </select>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button onClick={() => setBankModalOpen(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">
                                    Cancel
                                </button>
                                <button onClick={handleSaveBank} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">
                                    {isEditMode ? 'Update' : 'Add'} Bank Account
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default KhatabookBank;